import { z } from "zod";

import * as DbEnum from "../../../../generated/db-enum";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import { VibeTriggerSourceReadSchema } from "./enum-read.schemas";
import { VibeApprovalRequestStatusReadSchema } from "./status-read.schemas";

/**
 * `VibeApprovalRequestStatus`, from the generated enum tuple: a value added to
 * the Prisma enum reaches this contract without a hand-kept copy to update.
 * `DbEnum` is generated constants, so no Prisma runtime reaches the frontend.
 */
export const VibeApprovalRequestStatusSchema = z.enum(DbEnum.VIBE_APPROVAL_REQUEST_STATUS_VALUES);
export type VibeApprovalRequestStatusValue = z.infer<typeof VibeApprovalRequestStatusSchema>;

export const VibeApprovalDecisionKindSchema = z.enum(["APPROVE", "REJECT"]);
export type VibeApprovalDecisionKindValue = z.infer<typeof VibeApprovalDecisionKindSchema>;

/** The decision kind as a reader parses it: a kind this build does not list reads as unlisted. */
export const VibeApprovalDecisionKindReadSchema = enumReadSchema(["APPROVE", "REJECT"]);

/**
 * Reviewer note attached to a decision. Bounded — long-form
 * justification belongs in the linked deployment's description /
 * audit log, not inline on every approval row.
 */
export const VibeApprovalDecisionNoteSchema = z.string().trim().max(2_000, "note must be ≤ 2 KB");

export const VibeApprovalDecisionSchema = z.object({
  id: z.string().uuid(),
  vibeApprovalRequestId: z.string().uuid(),
  organizationId: z.string(),
  decision: VibeApprovalDecisionKindReadSchema,
  /// Nullable — the FK uses SET NULL on User delete so the decision
  /// row survives the reviewer's account being removed (audit
  /// evidence invariant). Live rows always carry a non-null value;
  /// the null state is exclusively historical for deleted reviewers.
  /// The audit feed's `deciderUserId` snapshot preserves the original
  /// identity in JSONB even after this column is nulled.
  decidedByUserId: z.string().nullable(),
  note: z.string().nullable(),
  decidedAt: z.coerce.date()
});

export type VibeApprovalDecisionDto = z.infer<typeof VibeApprovalDecisionSchema>;

export const VibeApprovalRequestSchema = z.object({
  id: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  organizationId: z.string(),
  status: VibeApprovalRequestStatusReadSchema,
  requiredApprovals: z.number().int().positive(),
  expiresAt: z.coerce.date(),
  decidedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeApprovalRequestDto = z.infer<typeof VibeApprovalRequestSchema>;

// ----------------------------------------------------------------------------
// Get approval request for a deployment
// ----------------------------------------------------------------------------

export const GetVibeApprovalRequestPathVarsSchema = z.object({
  appId: z.string().uuid(),
  deploymentId: z.string().uuid()
});

export const GetVibeApprovalRequestResponseSchema = z.object({
  request: VibeApprovalRequestSchema,
  decisions: z.array(VibeApprovalDecisionSchema)
});

// ----------------------------------------------------------------------------
// Record decision on an approval request
// ----------------------------------------------------------------------------

export const RecordVibeApprovalDecisionPathVarsSchema = z.object({
  appId: z.string().uuid(),
  deploymentId: z.string().uuid()
});

export const RecordVibeApprovalDecisionBodySchema = z.object({
  decision: VibeApprovalDecisionKindSchema,
  note: VibeApprovalDecisionNoteSchema.optional()
});

export const RecordVibeApprovalDecisionResponseSchema = z.object({
  request: VibeApprovalRequestSchema,
  decision: VibeApprovalDecisionSchema
});

// ----------------------------------------------------------------------------
// List PENDING approvals across the caller's org (reviewer dashboard data)
// ----------------------------------------------------------------------------

/**
 * Slim deployment/app context inlined on each reviewer-queue row. The bare
 * request only carries `vibeDeploymentId`; the console needs the owning
 * app (to deep-link into its deployment drawer and to label the row), the
 * deployment under review, and the requester (so the UI can disable
 * self-approval before the backend 403s it). Defined here — not composed
 * from `deployments.schemas` — because that module already imports this
 * one and a back-import would create a cycle. `triggerSource` is read through
 * `enum-read.schemas.ts` for the same reason: it is the same lenient read the
 * deployment itself carries, reached without importing the module that would
 * close the cycle.
 *
 * ⚠️ Slim is not the same as INCONSISTENT. `versionNumber` and `triggerSource`
 * were absent, so the reviewer queue rendered a deployment as an app name plus
 * seven characters of sha while every other surface named it `v37`. A reviewer
 * was therefore asked to approve a rollout without being shown the version they
 * would later roll back TO, or whether a human had asked for it. Both facts are
 * segments of the canonical record (`VibeDeploymentRecord`); a slim read may say
 * LESS than the full one, never something different.
 *
 * `status` is deliberately still absent: every row in this queue is PENDING by
 * definition, so a status badge here would be a column of one repeated value —
 * the same defect as a Scope column reading "All environments" twenty times.
 */
export const VibeApprovalRequestDeploymentContextSchema = z.object({
  vibeAppId: z.string().uuid(),
  appName: z.string(),
  /** The user-facing `v{n}` — what a rollback, a URL and a support question name. */
  versionNumber: z.number().int(),
  triggerSha: z.string(),
  triggerSource: VibeTriggerSourceReadSchema,
  createdByUserId: z.string().nullable()
});

export type VibeApprovalRequestDeploymentContextDto = z.infer<
  typeof VibeApprovalRequestDeploymentContextSchema
>;

export const VibeApprovalRequestListItemSchema = VibeApprovalRequestSchema.extend({
  deployment: VibeApprovalRequestDeploymentContextSchema
});

export type VibeApprovalRequestListItemDto = z.infer<typeof VibeApprovalRequestListItemSchema>;

export const ListPendingVibeApprovalsResponseSchema = z.object({
  requests: z.array(VibeApprovalRequestListItemSchema)
});
